import { motion } from 'framer-motion';
import { 
  Target, 
  Car, 
  FileSearch, 
  FileText, 
  Truck,
  Crosshair
} from 'lucide-react';

const services = [
  {
    icon: Target,
    title: 'Static ADAS Calibration',
    description: 'Precision calibration using OEM-specified targets and procedures for cameras, radar, and LiDAR systems in controlled workshop environments.',
    featured: true,
  },
  {
    icon: Car,
    title: 'Dynamic ADAS Calibration',
    description: 'Road-based calibration procedures for systems that require real-world driving conditions to achieve correct alignment.',
    featured: false,
  },
  {
    icon: FileSearch,
    title: 'Pre & Post-Repair Scanning',
    description: 'Comprehensive diagnostic scanning before and after collision repairs to identify all ADAS-related fault codes and system statuses.',
    featured: false,
  },
  {
    icon: FileText,
    title: 'OEM Documentation & Reporting',
    description: 'Complete calibration reports with OEM service information references, technician credentials, and audit-ready documentation.',
    featured: false,
  },
  {
    icon: Truck,
    title: 'Mobile Services',
    description: 'On-site calibration services for workshops and fleet operators. We bring the equipment to you for minimal vehicle downtime.',
    featured: false,
  },
  {
    icon: Crosshair,
    title: 'Workshop-Based Services',
    description: 'Full-service calibration at our equipped facility with controlled lighting, level floors, and complete target sets.',
    featured: false,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-4 block">
            Our Services
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4">
            ADAS Calibration Services
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-base sm:text-lg">
            Comprehensive calibration solutions for all Advanced Driver Assistance Systems, 
            delivered with OEM precision and insurer-grade documentation.
          </p>
        </motion.div>

        {/* Services Grid - Bento Layout */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                variants={itemVariants}
                className={`service-card bg-white border border-slate-200 rounded-lg p-6 md:p-8 ${
                  service.featured ? 'md:col-span-2 lg:col-span-1 lg:row-span-1 bg-slate-50' : ''
                }`}
                data-testid={`service-card-${index}`}
              >
                <div className="w-12 h-12 bg-blue-600/10 rounded-lg flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
